/** Emit events — the Bookings and Orders grid `emit_event` batch op.
 *
 *  - `GET /{bookings|orders}/batch/emit_event/catalog/` — the events the grid may
 *    emit (the codes delivery actually matches on), what context each needs, its
 *    gates, whether customer webhook subscriptions exist for it, and the run
 *    options. The dialog renders from it.
 *  - `POST /{bookings|orders}/batch/` with `action: 'emit_event'` — dispatch (via
 *    `runBatch`); params `EmitEventParams`. Dry run optional; it previews which
 *    notifications each event would match.
 *  - `GET /bgtasks/{id}/result/` — `EmitEventTaskResult`.
 *
 *  Hand-authored (console convention); mirrors `EmitEvent*` in
 *  console.openapi.yaml (contract test: emitEvents.contract.test.ts).
 *  Plan: backend aidocs/plans/2026-09-30-console-batch-emit-events.md.
 */
import { apiGet } from './client';

export type EmitEventGrid = 'bookings' | 'orders';
export type EmitEventFamily = 'tour_participant' | 'order';
export type EmitEventKind = 'lifecycle' | 'update' | 'timed';
export type EmitEventContextKind = 'none' | 'diff' | 'params';
export type EmitEventGate = 'trip_end' | 'precheckin_complete';
export type EmitEventSource = 'empty' | 'borrow' | 'json';

export interface EmitEventParamSpec {
  name: string;
  type: 'date' | 'time' | 'bool' | 'int';
  nullable: boolean;
  choices: number[];
  description: string;
}

export interface EmitEventCatalogEntry {
  /** What notifications are matched on — what ops pick. */
  code: string;
  /** What the event is built as (the emitters' spelling). */
  typename: string;
  family: EmitEventFamily;
  kind: EmitEventKind;
  phase: string;
  label: string;
  /** Timed events: when the cron emits it ("24h before trip start"). */
  timing: string | null;
  context: EmitEventContextKind;
  params: EmitEventParamSpec[];
  /** What source=empty uses. */
  params_defaults: string | null;
  evid: string | null;
  gates: EmitEventGate[];
  fires_webhooks: boolean;
}

export interface EmitEventCatalog {
  enabled: boolean;
  family: EmitEventFamily;
  max_selection: number;
  min_reason: number;
  tag: { prefix: string; max_user_length: number };
  statuses: Array<{ value: number; label: string }>;
  borrow: { default_window_days: number; max_candidates: number };
  gates: Record<string, string>;
  events: EmitEventCatalogEntry[];
}

export interface EmitEventBorrowOptions {
  statuses?: number[];
  after?: string | null;
  before?: string | null;
  skip_empty?: boolean;
}

export interface EmitEventContext {
  source: EmitEventSource;
  borrow?: EmitEventBorrowOptions;
  json?: Record<string, unknown>;
}

export interface EmitEventParams {
  event: string;
  tag: string;
  reason: string;
  dry_run: boolean;
  webhooks: boolean;
  force: boolean;
  context: EmitEventContext | null;
}

// ---------------------------------------------------------------------------
// Result
// ---------------------------------------------------------------------------

export type EmitEventOutcome =
  | 'emitted'
  | 'would_emit'
  | 'not_found'
  | 'events_disabled'
  | 'skipped_trip_end'
  | 'skipped_precheckin_incomplete'
  | 'build_error'
  | 'emit_error'
  | 'failed'
  | 'no_source_event'
  | 'source_archive_missing'
  | 'source_diff_empty'
  | 'source_unreadable'
  | 'source_lookup_timeout';

export interface EmitEventPreviewEntry {
  notification_id: number;
  name: string;
  methods: string[];
  match: boolean;
  reason?: string | null;
}

export interface EmitEventRecordContext {
  source?: EmitEventSource;
  affected_fields?: string[];
  params?: Record<string, unknown>;
  edl_id?: string;
  created_ts?: string;
  status?: number;
  status_label?: string;
  log_tag?: string | null;
  skipped?: Record<string, number>;
}

export interface EmitEventRecord {
  pk: number;
  ref: string;
  tenant?: string | null;
  outcome: EmitEventOutcome;
  detail?: string | null;
  logid?: string | null;
  event_delivery_uuid?: string | null;
  context?: EmitEventRecordContext | null;
  preview?: EmitEventPreviewEntry[];
}

export interface EmitEventTaskResult {
  total: number;
  dry_run: boolean;
  family: EmitEventFamily;
  event: string;
  typename: string;
  /** console-emit/<task id>/<tag> — the Delivery Logs tag. */
  tag: string;
  webhooks: boolean;
  force: boolean;
  context?: EmitEventContext | null;
  caveats: string[];
  counts: Partial<Record<EmitEventOutcome, number>>;
  records: EmitEventRecord[];
}

export const OUTCOME_LABELS: Record<EmitEventOutcome, string> = {
  emitted: 'Emitted',
  would_emit: 'Would emit',
  not_found: 'No longer exists',
  events_disabled: 'Events disabled for the tenant',
  skipped_trip_end: 'Skipped — booking has ended',
  skipped_precheckin_incomplete: 'Skipped — pre-check-in incomplete',
  build_error: 'Could not build the event',
  emit_error: 'Emit failed',
  failed: 'Failed',
  no_source_event: 'No earlier event to borrow from',
  source_archive_missing: 'Earlier event’s archive is gone',
  source_diff_empty: 'Every earlier event had an empty diff',
  source_unreadable: 'Earlier event’s context unreadable',
  source_lookup_timeout: 'Delivery-log lookup timed out',
};

export const OUTCOME_SEVERITY: Record<EmitEventOutcome, 'success' | 'info' | 'warning' | 'error'> = {
  emitted: 'success',
  would_emit: 'success',
  not_found: 'warning',
  events_disabled: 'warning',
  skipped_trip_end: 'warning',
  skipped_precheckin_incomplete: 'warning',
  build_error: 'error',
  emit_error: 'error',
  failed: 'error',
  no_source_event: 'warning',
  source_archive_missing: 'warning',
  source_diff_empty: 'warning',
  source_unreadable: 'error',
  source_lookup_timeout: 'error',
};

export const KIND_LABELS: Record<EmitEventKind, string> = {
  lifecycle: 'Booking lifecycle',
  update: 'Update',
  timed: 'Timed',
};

/** Background-task types of the two grids' emit runs (result panel registry). */
export const EMIT_EVENT_TASK_TYPES = ['console_booking_emit_events', 'console_order_emit_events'] as const;

export function fetchEmitEventCatalog(grid: EmitEventGrid): Promise<EmitEventCatalog> {
  return apiGet<EmitEventCatalog>(`/${grid}/batch/emit_event/catalog/`);
}
